import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import { api } from "../../lib/api";
import { useBooking } from "../../lib/BookingContext";

function fmtSummary(slot, service, location) {
  if (!slot || !service || !location) return "";
  const d = new Date(slot);
  const time = d.toLocaleTimeString("en-IE", { hour: "numeric", minute: "2-digit", hour12: true }).toLowerCase();
  const date = d.toLocaleDateString("en-IE", { month: "long", day: "numeric", year: "numeric" });
  return { time, date };
}

// Country codes Aerona accepts (verified against their patient details
// dropdown). Ireland kept first as the default; everything else in numeric
// order for predictability.
const COUNTRIES = [
  { code: "+353", flag: "🇮🇪", name: "Ireland" },
  { code: "+1",   flag: "🇺🇸", name: "United States / Canada" },
  { code: "+7",   flag: "🇷🇺", name: "Russia / Kazakhstan" },
  { code: "+31",  flag: "🇳🇱", name: "Netherlands" },
  { code: "+32",  flag: "🇧🇪", name: "Belgium" },
  { code: "+33",  flag: "🇫🇷", name: "France" },
  { code: "+34",  flag: "🇪🇸", name: "Spain" },
  { code: "+39",  flag: "🇮🇹", name: "Italy" },
  { code: "+40",  flag: "🇷🇴", name: "Romania" },
  { code: "+41",  flag: "🇨🇭", name: "Switzerland" },
  { code: "+44",  flag: "🇬🇧", name: "United Kingdom" },
  { code: "+45",  flag: "🇩🇰", name: "Denmark" },
  { code: "+49",  flag: "🇩🇪", name: "Germany" },
  { code: "+54",  flag: "🇦🇷", name: "Argentina" },
  { code: "+352", flag: "🇱🇺", name: "Luxembourg" },
  { code: "+356", flag: "🇲🇹", name: "Malta" },
  { code: "+357", flag: "🇨🇾", name: "Cyprus" },
  { code: "+358", flag: "🇫🇮", name: "Finland" },
  { code: "+370", flag: "🇱🇹", name: "Lithuania" },
  { code: "+371", flag: "🇱🇻", name: "Latvia" },
  { code: "+372", flag: "🇪🇪", name: "Estonia" },
  { code: "+380", flag: "🇺🇦", name: "Ukraine" },
  { code: "+420", flag: "🇨🇿", name: "Czech Republic" },
];

export default function StepDetails() {
  const ctx = useBooking();
  const { service, location, slot, patient, setPatient, next, back } = ctx;
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [matches, setMatches] = useState([]);
  const [picked, setPicked] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Guarantee +353 (Ireland) is the default country code whenever a patient
  // lands on this page with no phone entered. If they've already typed digits
  // their previous country choice is preserved (e.g. UK back-and-forward nav).
  useEffect(() => {
    if (!patient.phone && patient.phone_country !== "+353") {
      setPatient({ phone_country: "+353" });
    }
     
  }, []);

  const summary = fmtSummary(slot, service, location);
  const valid =
    patient.first_name.trim() &&
    patient.last_name.trim() &&
    patient.phone.replace(/\D/g, "").length >= 7 &&
    (!patient.email || /^\S+@\S+\.\S+$/.test(patient.email));

  // Fires a lightweight lead-capture POST so the abandoned-cart scanner
  // has a booking record to chase even if the customer never clicks any
  // payment button. Idempotent by (email+service+slot) so bouncing back
  // and forth on Step 3↔4 doesn't create duplicates. Fire-and-forget: we
  // never block the user's navigation if this fails.
  const saveDraft = async (aeronaPatientId, forceNew) => {
    try {
      const fullPhone = (patient.phone_country || "") + patient.phone;
      await api.saveBookingDraft({
        practice_id: location?.id,
        service_id: service?.id,
        service_name: service?.name,
        appointment_time: slot,
        first_name: patient.first_name.trim(),
        last_name: patient.last_name.trim(),
        email: (patient.email || "").trim(),
        phone: fullPhone,
        notes: patient.notes || "",
        aerona_patient_id: aeronaPatientId != null ? String(aeronaPatientId) : null,
        force_new_patient: !!forceNew,
      });
    } catch (_) { /* non-blocking */ }
  };

  const onContinue = async () => {
    setSubmitting(true);
    try {
      const fullPhone = patient.phone_country + patient.phone;
      const found = await api.searchPatients(fullPhone);
      if (found && found.length > 0) {
        setMatches(found);
        setPicked(String(found[0].id));
        setShowMatchModal(true);
        setSubmitting(false);
        return;
      }
      await saveDraft(null, false);
      next();
    } catch (e) {
      // If lookup fails, just proceed
      await saveDraft(null, false);
      next();
    } finally {
      setSubmitting(false);
    }
  };

  const confirmMatch = async (isMe) => {
    if (isMe) {
      const match = matches.find((m) => String(m.id) === picked);
      if (match) {
        setPatient({
          first_name: match.forename || patient.first_name,
          last_name: match.surname || patient.last_name,
          aerona_patient_id: match.id,
          force_new_patient: false,
        });
      }
    } else {
      // "Not Me, Continue" — force a brand-new Aerona patient record so e.g.
      // parents booking on behalf of their kids never get the kid's appointment
      // attached to the parent's existing patient profile.
      setPatient({
        aerona_patient_id: null,
        force_new_patient: true,
      });
    }
    setShowMatchModal(false);
    // Persist the lead-capture draft AFTER the modal-driven aerona_patient_id
    // is applied, so the draft carries the right patient linkage.
    // Read from the just-applied match (isMe branch) or force_new (not-me).
    const match = isMe ? matches.find((m) => String(m.id) === picked) : null;
    await saveDraft(match ? match.id : null, !isMe);
    next();
  };

  return (
    <div data-testid="step-details">
      <p className="text-[#5C5C5C] mb-2 leading-relaxed">
        You selected a booking for{" "}
        <strong className="text-[#1A1A1A]">
          {service?.price > 0
            ? `€${Number.isInteger(service.price) ? service.price : service.price.toFixed(2)} | `
            : "Free "}
          {service?.name}
        </strong>{" "}
        at <strong className="text-[#1A1A1A]">{location?.name}</strong> at{" "}
        <strong className="text-[#1A1A1A]">{summary.time}</strong> on{" "}
        <strong className="text-[#1A1A1A]">{summary.date}</strong>.
      </p>
      <p className="text-[#5C5C5C] mb-8">Please provide your details in the form below to proceed with booking.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
        <div>
          <label className="block text-sm font-semibold text-[#0466ff] mb-2" style={{ fontFamily: "Manrope" }}>
            First name <span className="text-[#C85A41]">(required)</span>
          </label>
          <input
            className="ws-input"
            value={patient.first_name}
            onChange={(e) => setPatient({ first_name: e.target.value })}
            data-testid="patient-first-name"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-[#0466ff] mb-2" style={{ fontFamily: "Manrope" }}>
            Last name <span className="text-[#C85A41]">(required)</span>
          </label>
          <input
            className="ws-input"
            value={patient.last_name}
            onChange={(e) => setPatient({ last_name: e.target.value })}
            data-testid="patient-last-name"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-[#0466ff] mb-2" style={{ fontFamily: "Manrope" }}>
            Phone <span className="text-[#D0021B] font-normal text-xs">— drop the leading zero please</span>
          </label>
          <div className="flex gap-2">
            <select
              className="ws-input !w-[110px] flex-shrink-0"
              value={patient.phone_country}
              onChange={(e) => setPatient({ phone_country: e.target.value })}
              data-testid="patient-phone-country"
            >
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
              ))}
            </select>
            <input
              type="tel"
              className="ws-input flex-1 min-w-0"
              placeholder="871234567"
              value={patient.phone}
              onChange={(e) => setPatient({ phone: e.target.value.replace(/[^\d]/g, "") })}
              data-testid="patient-phone"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-[#0466ff] mb-2" style={{ fontFamily: "Manrope" }}>
            Email
          </label>
          <input
            type="email"
            className="ws-input"
            value={patient.email}
            onChange={(e) => setPatient({ email: e.target.value })}
            data-testid="patient-email"
          />
        </div>
      </div>

      <div className="mt-8 sm:mt-10 flex items-center justify-between gap-3">
        <button onClick={back} className="ws-btn-primary inline-flex items-center gap-2" data-testid="details-back-button">
          <ArrowLeft className="w-4 h-4" strokeWidth={2.5} /> BACK
        </button>
        <button
          onClick={onContinue}
          disabled={!valid || submitting}
          className="ws-btn-primary inline-flex items-center gap-2"
          data-testid="details-next-button"
        >
          {submitting ? "CHECKING…" : "NEXT"} <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
        </button>
      </div>

      {showMatchModal && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center p-4"
          data-testid="patient-found-modal"
          onClick={() => setShowMatchModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E5E5]">
              <div className="font-bold" style={{ fontFamily: "Manrope" }}>Patient found</div>
              <button onClick={() => setShowMatchModal(false)} className="p-1 rounded hover:bg-[#FAFAFA]" data-testid="patient-modal-close">
                <X className="w-4 h-4 text-[#5C5C5C]" strokeWidth={1.5} />
              </button>
            </div>
            <div className="p-5">
              <p className="text-sm text-[#1A1A1A] mb-4 leading-relaxed">
                We think you have been a patient at our surgery before based on a matching phone number. Is this you?
              </p>
              <div className="space-y-2">
                {matches.map((m) => (
                  <label
                    key={m.id}
                    className="flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-[#FAFAFA] border border-[#E5E5E5]"
                    data-testid={`patient-match-${m.id}`}
                  >
                    <input
                      type="radio"
                      name="patient-match"
                      value={m.id}
                      checked={picked === String(m.id)}
                      onChange={(e) => setPicked(e.target.value)}
                      className="accent-[#0466ff]"
                    />
                    <span className="text-sm">{m.full_name || `${m.forename || ""} ${m.surname || ""}`.trim() || "(name unavailable)"}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex flex-col-reverse sm:flex-row gap-3 px-5 py-4 bg-[#FAFAFA] border-t border-[#E5E5E5] justify-end">
              <button
                onClick={() => confirmMatch(false)}
                className="px-5 py-2.5 rounded-full font-bold uppercase tracking-wider border-2 border-[#0466ff] text-[#1A1A1A] bg-white hover:bg-[#F0F6FF] transition-colors text-sm"
                data-testid="patient-not-me-button"
              >
                NOT ME, CONTINUE
              </button>
              <button
                onClick={() => confirmMatch(true)}
                className="px-5 py-2.5 rounded-full font-bold uppercase tracking-wider text-white bg-[#22C55E] hover:bg-[#16A34A] transition-colors text-sm shadow-sm"
                data-testid="patient-is-me-button"
              >
                THIS IS ME, CONTINUE
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
